import { PauseIcon, PlayIcon } from '@heroicons/react/solid';
import Image from 'next/image';
import type { CSSProperties } from 'react';

import clsxm from '@/lib/clsxm';

import {
  getMusicPresetPreview,
  type MusicPreset,
} from '@/constants/musicPresets';

import type { MusicT, PreviewPlayer } from './kit';

/**
 * A ready-made sound as an album tile: its cover, a play button for the
 * 30-second preview and "Use this sound", which starts a song with it.
 */
export default function SongCard({
  preset,
  player,
  onUse,
  t,
  useLabel,
  size = 'md',
  className,
  style,
}: {
  preset: MusicPreset;
  player: PreviewPlayer;
  onUse: (preset: MusicPreset) => void;
  t: MusicT;
  /** Label of the button that starts a song with this sound. */
  useLabel: string;
  size?: 'sm' | 'md';
  className?: string;
  style?: CSSProperties;
}) {
  const preview = getMusicPresetPreview(preset.id);
  const playing = !!preview && player.playingSrc === preview.audio;
  const title = t(preset.title);

  return (
    <div className={clsxm('group min-w-0', className)} style={style}>
      <div
        className='relative aspect-square overflow-hidden rounded-2xl shadow-[0_18px_40px_-24px_rgba(15,15,40,0.55)] ring-1 ring-black/5'
        style={{
          backgroundImage: `linear-gradient(135deg, ${preset.gradient[0]}, ${preset.gradient[1]})`,
        }}
      >
        {preview && (
          <Image
            src={preview.cover}
            alt={t('Cover art for {{name}}', { name: title })}
            fill
            unoptimized
            loading='lazy'
            sizes='(max-width: 640px) 45vw, (max-width: 1024px) 30vw, 220px'
            className='object-cover transition-transform duration-700 group-hover:scale-[1.05]'
          />
        )}
        <div className='pointer-events-none absolute inset-0 bg-gradient-to-t from-black/55 via-black/0 to-black/0' />
        {preview && (
          <button
            type='button'
            onClick={() => player.toggle(preview.audio)}
            aria-label={`${playing ? t('Pause') : t('Play')}: ${title}`}
            aria-pressed={playing}
            className={clsxm(
              'absolute bottom-3 left-3 flex items-center justify-center rounded-full bg-white text-[#10101c] shadow-lg ring-1 ring-black/5 transition-transform duration-150 hover:scale-105 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 active:scale-95',
              size === 'sm' ? 'h-9 w-9' : 'h-11 w-11',
            )}
          >
            {playing ? (
              <PauseIcon className={size === 'sm' ? 'h-4 w-4' : 'h-5 w-5'} />
            ) : (
              <PlayIcon
                className={clsxm(
                  'translate-x-px',
                  size === 'sm' ? 'h-4 w-4' : 'h-5 w-5',
                )}
              />
            )}
          </button>
        )}
        {playing && (
          <div className='absolute inset-x-3 bottom-[3.75rem] h-1 overflow-hidden rounded-full bg-white/30'>
            <div
              className='h-full rounded-full bg-white'
              style={{ width: `${Math.round(player.progress * 100)}%` }}
            />
          </div>
        )}
        {preset.instrumental && (
          <span className='absolute right-3 top-3 rounded-full bg-black/45 px-2 py-0.5 text-[10px] font-medium text-white backdrop-blur'>
            {t('Instrumental')}
          </span>
        )}
      </div>
      <div className='mt-3 flex items-start justify-between gap-2'>
        <div className='min-w-0'>
          <h3
            className={clsxm(
              'truncate font-semibold text-[#10101c] dark:text-white',
              size === 'sm' ? 'text-sm' : 'text-[15px]',
            )}
          >
            {title}
          </h3>
          <p className='mt-0.5 line-clamp-2 text-[13px] leading-snug text-[#6f6f86] dark:text-[#7b7b96]'>
            {t(preset.description)}
          </p>
        </div>
      </div>
      <button
        type='button'
        onClick={() => onUse(preset)}
        className='mt-2.5 inline-flex items-center gap-1 text-[13px] font-semibold text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300'
      >
        {useLabel}
        <span aria-hidden='true' className='inline-block rtl:-scale-x-100'>
          →
        </span>
      </button>
    </div>
  );
}
